// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The embedding matrix and the similarity over it.
 *
 * One `Float32Array` of `count * dim`, row-major, L2-normalised in place. Rows are normalised
 * ONCE at write time, which turns cosine similarity into a bare dot product — and makes the
 * exported `embeddings.f32` immediately usable without the caller having to know whether the
 * vectors were normalised.
 */

export class EmbeddingMatrix {
  readonly dim: number;
  private data: Float32Array;
  private count = 0;

  constructor(dim: number, capacity: number) {
    this.dim = dim;
    this.data = new Float32Array(dim * Math.max(1, capacity));
  }

  get size(): number {
    return this.count;
  }

  /** The live buffer, exactly `size * dim` long. Do not retain across a `push`. */
  view(): Float32Array {
    return this.data.subarray(0, this.count * this.dim);
  }

  /**
   * Append one vector, normalising it.
   *
   * A zero vector cannot be normalised, and it is not a theoretical case: a fully black or fully
   * saturated frame can produce one on a poorly quantised model. It is stored as-is and reported
   * as similar to nothing, which is the honest behaviour — the alternative, dividing by zero,
   * yields NaN and NaN >= threshold is false everywhere, so it would look identical while being
   * an accident rather than a decision.
   */
  push(vec: ArrayLike<number>): number {
    if (vec.length !== this.dim) throw new Error(`expected ${this.dim} dims, got ${vec.length}`);
    const need = (this.count + 1) * this.dim;
    if (need > this.data.length) {
      const grown = new Float32Array(Math.max(need, this.data.length * 2));
      grown.set(this.data);
      this.data = grown;
    }
    const off = this.count * this.dim;
    let sum = 0;
    for (let i = 0; i < this.dim; i++) sum += vec[i]! * vec[i]!;
    const norm = Math.sqrt(sum);
    const scale = norm > 0 ? 1 / norm : 0;
    for (let i = 0; i < this.dim; i++) this.data[off + i] = vec[i]! * scale;
    return this.count++;
  }

  /** Cosine similarity. Rows are unit vectors, so this is a dot product. */
  similarity(a: number, b: number): number {
    const { dim, data } = this;
    const oa = a * dim;
    const ob = b * dim;
    let dot = 0;
    for (let i = 0; i < dim; i++) dot += data[oa + i]! * data[ob + i]!;
    return dot;
  }

  /** A copy sized exactly to the contents — what gets written to `embeddings.f32`. */
  toBytes(): Uint8Array {
    const exact = this.data.slice(0, this.count * this.dim);
    return new Uint8Array(exact.buffer, exact.byteOffset, exact.byteLength);
  }
}

/**
 * Every pair above a cosine threshold.
 *
 * This is the only quadratic step in the tool. It is left as a plain double loop because the
 * arithmetic says it does not need to be anything cleverer: at the stated 2,000-image ceiling and
 * 512 dimensions, the upper triangle is 2,000 x 1,999 / 2 = 1,999,000 pairs at 512 multiply-adds
 * each, so about 1.0 x 10^9 flops. In a Worker on typed arrays that is a couple of seconds — far
 * less than the decode pass it follows, and not worth the risk of an approximate index that could
 * silently miss a duplicate.
 */
export function pairsAboveCosine(
  m: EmbeddingMatrix,
  threshold: number,
  onPair: (a: number, b: number, cosine: number) => void,
  onProgress?: (done: number, total: number) => void,
): void {
  const n = m.size;
  for (let a = 0; a < n; a++) {
    for (let b = a + 1; b < n; b++) {
      const c = m.similarity(a, b);
      if (c >= threshold) onPair(a, b, c);
    }
    if (onProgress && (a & 31) === 0) onProgress(a, n);
  }
  onProgress?.(n, n);
}
